// import { Database } from './../../../models/database';
import { ColumnResponse, Database, DatabaseName, MetadataPayload, FAQItem, ScriptName, SnowDatabases, SnowSchema, SnowTable, viewFile, CodeGenerationRequest, DataGenerationRequest, UploadFileMetadata, TestConnectionResponse, viewMetaData } from '../../../models/database';
import { HttpClient, HttpErrorResponse, HttpHandler, HttpHeaderResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { tap, timeout, retry, delay } from 'rxjs/operators';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment'

@Injectable({
  providedIn: 'root'
})
export class DatabaseService {

  private postResponseSubject = new BehaviorSubject<any>(null);
  public postResponse$ = this.postResponseSubject.asObservable();
  private postCodeResponseSubject = new BehaviorSubject<any>(null);
  public postCodeResponse$ = this.postCodeResponseSubject.asObservable()
  private postScriptResponseSubject = new BehaviorSubject<any>(null);
  public postScriptResponse$ = this.postScriptResponseSubject.asObservable()
  private uploadResponseSubject = new BehaviorSubject<any>(null);
  public uploadResponse$ = this.uploadResponseSubject.asObservable();

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private router: Router
  ) { }

  private getSessionHeader(): {headers: HttpHeaders } {
    const sessionId = sessionStorage.getItem('Session_Id')
    return {
      headers: new HttpHeaders ({
        'X-Session-ID': `${sessionId || ''}`
      })
    }
  }

  private handleApiError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.status === 0) {
      errorMessage = 'Network error: Please check your internet connection';
    } else if (error.status === 401) {
      errorMessage = 'Session expired. Please login again.';
      setTimeout(() => this.router.navigate(['/loginpage']),2000)
      // this.authService.logout();
    } else if (error.status === 403) {
      errorMessage = 'You do not have permission for this action';
    } else if (error.status === 404) {
      errorMessage = 'Content not found';
    } else if (error.status === 500) {
      errorMessage = 'Server Busy: Please try again';
    } else if (error.status >= 500) {
      errorMessage = 'Server error: Please try again later';
    }

    this.toastService.showToast(errorMessage, {
      classname: 'bg-danger text-light',
      delay: 5000
    });

    return throwError(() => error);
  }

  // -----------------------------------------------------------------------------------------------//
  //Backend API URL
  private baseURL = environment.backendApiUrl;
  // -----------------------------------------------------------------------------------------------//

  // HELP SECTION
  private helpAPIurl = `${this.baseURL}/help`
  // GETTING QUICK START & FAQ SECTION
  getQUCIKSTART(): Observable<any> {
    return this.http.get<any>(`${this.helpAPIurl}/quickstart`).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  getFAQSection(): Observable<FAQItem[]> {
    return this.http.get<FAQItem[]>(`${this.helpAPIurl}/faq`).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  // -----------------------------------------------------------------------------------------------//

  // DATA MANAGEMENT API's
  private dbAPIurl = `${this.baseURL}/databases`;

  //GETTING DETAILS OF DATABASES API
  getDatabases(): Observable<Database[]> {
    const sessionId = this.getSessionHeader()
    return this.http.get<Database[]>(`${this.dbAPIurl}`, sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  //GETTING LIST OF DATABASES NAMES API
  listOfDatabases(): Observable<DatabaseName[]> {
    const sessionId = this.getSessionHeader()
    return this.http.get<DatabaseName[]>(`${this.dbAPIurl}/names`, sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  //ADDING DATABASES API
  addDatabase(platform_name:string, dbDetails: any): Observable<any> {
    const sessionId = this.getSessionHeader()
    return this.http.post<Database[]>(`${this.dbAPIurl}/${platform_name}`, dbDetails, sessionId).pipe(
      // tap(() => {
      //   this.toastService.showToast('Database added successfully', {
      //     classname: 'bg-success text-light',
      //     delay: 3000
      //   });
      // }),
      catchError(this.handleApiError.bind(this))
    );
  };

  //TESTING DATABASE CONNECTION
  testConnection(platform_name: any): Observable<any> {
    const sessionId = this.getSessionHeader()
    return this.http.post<TestConnectionResponse>(`${this.dbAPIurl}/${platform_name}/test`, {}, sessionId).pipe(
      // tap(() => {
      //   this.toastService.showToast('Coonection test successful', {
      //     classname: 'bg-success text-light',
      //     delay: 3000
      //   });
      // }),
      catchError(this.handleApiError.bind(this))
    );
  }

  //UPDATING EXISTING DETAILS OF DATABASE
  updateDatabaseDetails(platform_name: any, payload: any): Observable<any> {
    const sessionId = this.getSessionHeader()
    return this.http.put<void>(`${this.dbAPIurl}/${platform_name}`,payload, sessionId).pipe(
      // tap(() => {
      //   this.toastService.showToast('Database updated successfully', {
      //     classname: 'bg-success text-light',
      //     delay: 3000
      //   });
      // }),
      catchError(this.handleApiError.bind(this))
    );
  }

  //DELETING EXISTING DATABASE
  deleteDatabase(platform_name: any): Observable<any> {
    const sessionId = this.getSessionHeader()
    return this.http.delete<DatabaseName[]>(`${this.dbAPIurl}/${platform_name}`, sessionId).pipe(
      // tap(() => {
      //   this.toastService.showToast('Database deleted successfully', {
      //     classname: 'bg-success text-light',
      //     delay: 3000
      //   });
      // }),
      catchError(this.handleApiError.bind(this))
    );
  }


  // -----------------------------------------------------------------------------------------------//
  //METADATA API'S
  private metadataAPIurl =  `${this.baseURL}/metadata`
  // SNOWFLAKE GET DATABASES
  snowDatabases(platform_name: any): Observable<SnowDatabases> {
    const sessionId = this.getSessionHeader()
    return this.http.get<SnowDatabases>(`${this.metadataAPIurl}/${platform_name}/databases`, sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  // SNOWFLAKE GET SCHEMAS
  snowSchemas(platform_name:any, database: string): Observable<SnowSchema> {
    const sessionId = this.getSessionHeader()
    return this.http.get<SnowSchema>(`${this.metadataAPIurl}/${platform_name}/${database}/schemas`, sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  // SNOWFLAKE GET TABLES
  snowTables(platform_name:any, database: string, schema: string): Observable<SnowTable> {
    const sessionId = this.getSessionHeader()
    return this.http.get<SnowTable>(`${this.metadataAPIurl}/${platform_name}/${database}/${schema}/tables`,sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  // SNOWFLAKE GET TABLES COLUMNS
  snowColumns(platform_name:any, database: string, schema: string, table: string): Observable<ColumnResponse> {
    const sessionId = this.getSessionHeader()
    return this.http.get<ColumnResponse>(`${this.metadataAPIurl}/${platform_name}/${database}/${schema}/${table}/metadata`, sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  // STORING SNOWFLAKE METADATA
  storeSnowData(payload: MetadataPayload): Observable<any> {
    const sessionId = this.getSessionHeader()
    return this.http.post(`${this.metadataAPIurl}/save`, payload, sessionId).pipe(
      tap((response) => {
        this.postResponseSubject.next(response)
        this.toastService.showToast("Metadata stored Successfully", {classname: 'bg-success text-light', delay: 3000})
      }), catchError(this.handleApiError.bind(this))
    )
  }

  // -----------------------------------------------------------------------------------------------//
  // FILE API's
   private filesAPIurl = `${this.baseURL}/files`
  // UPLOADING DATA SOURCE FILES
  uploadFiles(files: File[], file_metadata: UploadFileMetadata[]): Observable<any> {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files`, file, file.name);
      formData.append('metadata', JSON.stringify(file_metadata))
    })
   // formData.append('metadata', JSON.stringify(metadata));
   const sessionId = this.getSessionHeader()
    return this.http.post(`${this.filesAPIurl}/upload`, formData, sessionId).pipe(
      tap(response => {
        this.uploadResponseSubject.next(response);
        this.toastService.showToast("Files uploaded Successfully", {classname: 'bg-success text-light', delay: 3000})
      }),
      catchError(this.handleApiError.bind(this))
    )
  }

  fileColumns(database: string, schema: string, table: string):Observable<ColumnResponse>{
    const sessionId = this.getSessionHeader()
    return this.http.get<any>(`${this.filesAPIurl}/file-metadata/${database}/${schema}/${table}`,sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

  // -----------------------------------------------------------------------------------------------//
  // SCRIPT API's
   private scriptAPIurl = `${this.baseURL}/scripts`
  //GETTING SCRIPT NAME & CONTENT
  getScriptNames(params?:{sort_by?: string, limit?:number}): Observable<ScriptName>{
    const httpParams = new HttpParams({ fromObject: params || {} })
    const options = {
      headers: new HttpHeaders({
        'X-Session-ID': `${sessionStorage.getItem('Session_Id') || ''}`
      }),
      params: httpParams
    };

    return this.http.get<ScriptName>(`${this.scriptAPIurl}/list`, options).pipe(
      catchError(this.handleApiError.bind(this))
    )
  }

  getScriptContent(filename: string, file_type: 'script' | 'validation'):Observable<viewFile | null>{
    const endpoint = file_type === 'script' ? 'script' : 'validation'
    const sessionId = this.getSessionHeader()
    return this.http.get<viewFile>(`${this.scriptAPIurl}/view/${filename}?file_type=${endpoint}`,sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    )
  }

  getMetadataContent(filepath: string):Observable<viewMetaData | null>{
    const sessionId = this.getSessionHeader()
    return this.http.get<viewMetaData>(`${this.scriptAPIurl}/view/${filepath}`, sessionId).pipe(
      catchError(this.handleApiError.bind(this))
    )
  }

  //GENERATING CODE & POSTING PARAMETERS
  // generateCode(payLoad: CodeGenerationRequest): Observable<any>{
  //   const sessionId = this.getSessionHeader()
  //   return this.http.post(`${this.scriptAPIurl}/generate`, payLoad, sessionId).pipe(
  //     timeout(360000),
  //     tap((response) => {
  //       this.postCodeResponseSubject.next(response)
  //       this.toastService.showToast("Code generated successfully",{classname: 'bg-success text-light', delay: 3000})
  //     }),
  //     catchError(this.handleApiError.bind(this))
  //   )
  // }
  generateCode(payLoad: CodeGenerationRequest): Observable<any>{
    const sessionId = this.getSessionHeader()
    
    const options = {
      ...sessionId,
      headers: sessionId.headers.set('Content-Type', 'application/json')
    };
  
    return this.http.post(`${this.scriptAPIurl}/generate`, payLoad, options).pipe(
      timeout(360000), // 6 minutes timeout (longer than backend)
      tap((response) => {
        this.postCodeResponseSubject.next(response)
        this.toastService.showToast("Code generated successfully",{classname: 'bg-success text-light', delay: 3000})
      }),
      catchError((error) => {
        if (error.name === 'TimeoutError') {
          this.toastService.showToast("The operation is taking longer than expected. Please try with fewer rows.", {
            classname: 'bg-warning text-light', 
            delay: 8000
          });
        }
        return this.handleApiError(error);
      })
    )
  }

  // -----------------------------------------------------------------------------------------------//
  private dataAPIurl = `${this.baseURL}/data-generation`
  //GENERATING DATA FROM SCRIPT NAME
  generateData(payLoad: DataGenerationRequest): Observable<any>{ //, {responseType : 'text'}
    const sessionId = this.getSessionHeader()
    return this.http.post(`${this.dataAPIurl}/execute`, payLoad, sessionId).pipe(
      // tap(() => {
      //   this.toastService.showToast("Data generated successfully",{classname: 'bg-success text-light', delay: 3000})
      // }),
      catchError(this.handleApiError.bind(this))
    )
  }

  saveWorkflow(scriptName: string): Observable<any> {
    const sessionId = this.getSessionHeader();
    const payload = {
      script_name: scriptName,
      // Add any other required fields
    };
  
    return this.http.post(`${this.dataAPIurl}/save`, payload, sessionId).pipe(
      // catchError(error => {
      //   console.error('Error saving workflow:', error);
      //   return throwError(() => error);
      // })
      catchError(this.handleApiError.bind(this)) 
    );
  }

  // Add a new method specifically for downloading the file with existing ID
  downloadGeneratedFile(fileId: string): Observable<Blob> {
    const sessionId = this.getSessionHeader();
    
    return this.http.get(`${this.dataAPIurl}/download/${fileId}`, {
      ...sessionId,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleApiError.bind(this))
    );
  }

}

